// eslint-disable-next-line eag/no-external-date-time-format-functions -- Custom format
import {format as formatDate, startOfToday} from 'date-fns';
import {Button, showNotification} from 'platform/components';
import {Heading, HStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {head, isEmpty} from 'ramda';
import {isString} from 'ramda-adjunct';

import {
  useGetAuctionUserToBuyQuery,
  useSourcingExportAllUserToBuyToCsvMutation,
  useSourcingListUserToBuyQuery,
  useSourcingPushVehiclesToSaleMutation,
} from '@dms/api/core';
import {useGetUserStatsQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {COUNTRY_CZE, CURRENCY_CZK, formatDocumentTitle, handleApiError} from '@dms/shared';

import {AuctionPreview} from '../../components/AuctionPreview/AuctionPreview';
import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../components/SourcingTable/SourcingTable';
import {createSourcingTableQueryResult} from '../../components/SourcingTable/utils/createSourcingTableQueryResult';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {
  AUCTION_USER_LIST_ORDER_BY_MAP,
  AUCTION_USER_LIST_SORT_OPTIONS,
} from '../../constants/auctionSortOptions';
import {
  CLASSIFIEDS_USER_LIST_ORDER_BY_MAP,
  CLASSIFIEDS_USER_LIST_SORT_OPTIONS,
} from '../../constants/classifiedsSortBy';
import {USER_LIST_DEFAULT_ORDER_ID} from '../../constants/USER_LIST_DEFAULT_ORDER_ID';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useCurrentWorkspace} from '../../hooks/useCurrentWorkspace';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {downloadCsv} from '../../utils/downloadCsv';
import {getAuctionTableColumns} from '../../utils/getAuctionTableColumns';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';

export function UserToBuyPage() {
  const formatNumber = useFormatNumber();
  const enumLookup = useAuctionEnumLookup();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const sourcingType = useSourcingType();
  const currentWorkspace = useCurrentWorkspace();
  const [classifiedsState, classifiedsDispatch] = useSourcingTable(
    'user-to-buy',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [auctionsState, auctionsDispatch] = useSourcingTable(
    'user-to-buy-auctions',
    USER_LIST_DEFAULT_ORDER_ID
  );
  const [state, dispatch] =
    sourcingType === SourcingType.CLASSIFIEDS
      ? [classifiedsState, classifiedsDispatch]
      : [auctionsState, auctionsDispatch];

  const sortByOptions =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_SORT_OPTIONS
      : CLASSIFIEDS_USER_LIST_SORT_OPTIONS;
  const orderBy =
    sourcingType === SourcingType.AUCTIONS
      ? AUCTION_USER_LIST_ORDER_BY_MAP.get(state.orderBy)
      : CLASSIFIEDS_USER_LIST_ORDER_BY_MAP.get(state.orderBy);

  const classifiedsQueryResult = useSourcingListUserToBuyQuery(
    {
      orderBy: isEmpty(orderBy) ? undefined : orderBy?.join(','),
      currency: currency?.code ?? CURRENCY_CZK,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      size: 1000,
    },
    {skip: sourcingType === SourcingType.AUCTIONS}
  );

  const auctionsQueryResult = useGetAuctionUserToBuyQuery(
    {
      orderBy: isEmpty(orderBy) ? undefined : orderBy,
      currency: currency?.code ?? CURRENCY_CZK,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      size: 1000,
    },
    {skip: sourcingType === SourcingType.CLASSIFIEDS}
  );

  const {data: userStatistics} = useGetUserStatsQuery();

  const toBuyCount =
    match(sourcingType)
      .with(SourcingType.CLASSIFIEDS, () => userStatistics?.userStats?.toBuyCount)
      .with(SourcingType.AUCTIONS, () => userStatistics?.userAuctionStats?.toBuyCount)
      .exhaustive() ?? 0;

  const [pushVehiclesToSale] = useSourcingPushVehiclesToSaleMutation();
  const [exportUserToBuyToCsv, {isFetching: isExporting}] =
    useSourcingExportAllUserToBuyToCsvMutation();

  const sourcingColumns = useSourcingTableColumns();
  const auctionColumns = getAuctionTableColumns({formatNumber, enumLookup});
  const columns = sourcingType === SourcingType.CLASSIFIEDS ? sourcingColumns : auctionColumns;
  const tableQueryResult = createSourcingTableQueryResult({
    sourcingType,
    enumLookup,
    classifiedsQueryResult,
    auctionsQueryResult,
  });
  const rows = tableQueryResult.data;

  const handleExport = () => {
    exportUserToBuyToCsv({
      currency: currency?.code,
      buyerCountry: targetCountry?.code,
    })
      .unwrap()
      .then((csvContent) => {
        if (isString(csvContent)) {
          downloadCsv(
            csvContent,
            `${currentWorkspace?.name}_${formatDate(startOfToday(), 'yyyyMMdd')}.csv`
          );
          return;
        }

        throw new Error('CSV content must be string.');
      })
      .catch(handleApiError);
  };

  const handleBuy = () => {
    pushVehiclesToSale({
      externalIds: rows
        .filter((row) => row.sourcingVehicle.isAvailable)
        .map(({sourcingVehicle}) => sourcingVehicle?.adId),
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('entity.vehicle.notifications.vehiclesPushedToSales'))
      )
      .catch(handleApiError);
  };

  const selectedAdId = state.selectedRow?.sourcingVehicle.adId;
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  return (
    <SourcingLayout
      header={
        <HStack align="center" justify="space-between">
          <Heading size={2} alternative>
            {i18n.t('entity.vehicle.labels.toBuyPage')}
          </Heading>
          <HStack spacing={2}>
            <Button
              title={`${i18n.t('general.actions.exportAll')} (${toBuyCount})`}
              variant="outlined"
              leftIcon="content/save_alt"
              isDisabled={isExporting}
              isLoading={isExporting}
              onClick={handleExport}
              data-testid={testIds.sourcing.classifieds('shoppingList-exportButton')}
            />
            <Button
              variant="primary"
              leftIcon="maps/local_mall"
              title={`${i18n.t('general.actions.buyNowAll')} (${toBuyCount})`}
              onClick={handleBuy}
              data-testid={testIds.sourcing.classifieds('shoppingList-buyButton')}
            />
          </HStack>
        </HStack>
      }
      content={
        <SourcingDataStatus
          iconUrl="/assets/images/empty_images/empty-basket.svg"
          text={i18n.t('entity.vehicle.notifications.nothingToBuy')}
          isEmpty={!tableQueryResult.isLoading && isEmpty(rows)}
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            sortByOptions={sortByOptions}
            queryResult={tableQueryResult}
          />
        </SourcingDataStatus>
      }
      iframe={
        sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(preview) ? (
          <AuctionPreview key={preview.auctionVehicle.adId} auction={preview} />
        ) : (
          <VehiclePreview
            key={getSourcingVehicleId(preview)}
            vehicleId={getSourcingVehicleId(preview)}
          />
        )
      }
      pageTitle={formatDocumentTitle(
        i18n.t('entity.vehicle.labels.toBuyPage'),
        sourcingType === SourcingType.CLASSIFIEDS
          ? i18n.t('page.sourcing.labels.classifieds')
          : i18n.t('page.sourcing.labels.auctions'),
        i18n.t('page.sourcing.labels.sourcing')
      )}
    />
  );
}
